import type {
  Channel,
  ChannelSettingSummary,
  MeResponse,
  ReasonCode,
  SubscriptionId,
  SubscriptionSummary,
} from '../../api';
import type { StatusTone } from '../../components/StatusChip';

/** A channel with a current subscription: a row of "Copied" (S9 R2). */
export type CopiedChannel = Channel & { readonly subscription: SubscriptionSummary };

/** True for a channel the trader copies (it has a current subscription). */
export function isCopied(channel: Channel): channel is CopiedChannel {
  return channel.subscription !== null;
}

/**
 * The chip of a copied row: Home's label of the state (`home.channels.copyState.*`, M1 R8), or
 * E3's "Inactive" for a channel the trader can no longer reach (S9 R3, FXM-01 §5).
 */
export type CopiedChip =
  'active' | 'paused' | 'to_configure' | 'suspended_limit' | 'suspended_external' | 'inactive';

/** The line under a copied channel's name (S9 R4). */
export type CopiedLine =
  | { readonly kind: 'summary'; readonly setting: ChannelSettingSummary }
  | { readonly kind: 'notConfigured' }
  | { readonly kind: 'reason'; readonly reasonCode: ReasonCode }
  | { readonly kind: 'none' };

/** What a copied row shows. */
export interface CopiedRowView {
  readonly subscriptionId: SubscriptionId;
  readonly chip: CopiedChip;
  readonly tone: StatusTone;
  readonly line: CopiedLine;
}

// C3 R12: the state-to-tone mapping lives in the feature; the same as Home's (M1, mockup Main).
const TONE: Record<CopiedChip, StatusTone> = {
  active: 'success',
  paused: 'neutral',
  to_configure: 'warning',
  suspended_limit: 'warning',
  suspended_external: 'danger',
  inactive: 'danger',
};

/**
 * The chip and line of a copied channel (S9 R3, R4). Inaccessible wins over any status (Q44:
 * the API reads a left channel as `SUSPENDED_EXTERNAL` / `CHANNEL_INACCESSIBLE`). The summary is
 * shown for an active or paused channel when `GET /me` has it (Q122). The switch is exhaustive:
 * a new status fails the type check (standards §1).
 */
export function copiedRowView(
  channel: CopiedChannel,
  setting: ChannelSettingSummary | undefined,
): CopiedRowView {
  const { subscription } = channel;
  const view = (chip: CopiedChip, line: CopiedLine): CopiedRowView => ({
    subscriptionId: subscription.id,
    chip,
    tone: TONE[chip],
    line,
  });
  if (!channel.accessible) {
    return view('inactive', { kind: 'reason', reasonCode: 'CHANNEL_INACCESSIBLE' });
  }
  const summary: CopiedLine =
    setting === undefined ? { kind: 'none' } : { kind: 'summary', setting };
  // C1 §5.2: a suspended status always carries its cause (the schema refuses one without).
  const cause: CopiedLine =
    subscription.suspensionReasonCode === null
      ? { kind: 'none' }
      : { kind: 'reason', reasonCode: subscription.suspensionReasonCode };
  switch (subscription.status) {
    case 'NOT_CONFIGURED':
      return view('to_configure', { kind: 'notConfigured' });
    case 'ACTIVE':
      return view('active', summary);
    case 'PAUSED':
      return view('paused', summary);
    case 'SUSPENDED_LIMIT':
      return view('suspended_limit', cause);
    case 'SUSPENDED_EXTERNAL':
      return view('suspended_external', cause);
  }
}

/**
 * The two sections of `GestionCanaux.html` (S9 R2; Q30, Q88, provisional): copied channels, the
 * accessible ones in API order then the inactive ones, then the others in API order (recommended
 * first, S5 R12).
 */
export function tabSections(channels: readonly Channel[]): {
  readonly copied: readonly CopiedChannel[];
  readonly others: readonly Channel[];
} {
  const copied = channels.filter(isCopied);
  return {
    copied: [...copied.filter((c) => c.accessible), ...copied.filter((c) => !c.accessible)],
    others: channels.filter((c) => !isCopied(c)),
  };
}

/**
 * The setting summary of each channel `GET /me` knows, by subscription id (S9 R4; Proposal, to be
 * validated, Q122). `GET /me` reads its channels from the engine (Q108): a channel it does not
 * list simply has no summary.
 */
export function settingsBySubscription(
  me: MeResponse | undefined,
): ReadonlyMap<string, ChannelSettingSummary> {
  const map = new Map<string, ChannelSettingSummary>();
  for (const item of me?.channels ?? []) {
    if (item.setting !== null) map.set(item.subscriptionId, item.setting);
  }
  return map;
}
